/**
 * Working out an expiry on the client.
 *
 * This mirrors `api/core/src/shelf_life.rs`, and it is the one piece of domain logic that
 * exists in both languages. That duplication is deliberate but not free:
 *
 * The server is authoritative. This copy exists only so that an item added while standing in
 * a cold garage shows its date immediately instead of blank until the next sync — which is
 * the entire promise of a local-first client. `GET /v1/foods` hands over the shelf-life table
 * for exactly this purpose, so the *data* is never duplicated, only the arithmetic.
 *
 * If the rule changes, it changes here too, and the tests below mirror the Rust ones so the
 * drift shows up as a failure rather than as a quietly wrong date.
 */

import type { Food, Grocery, LocationKind, ShelfLife } from '../api/types';

export function daysIn(shelfLife: ShelfLife, location: LocationKind): number | null {
  switch (location) {
    case 'fridge':
      return shelfLife.fridge;
    case 'freezer':
      return shelfLife.freezer;
    // A spice drawer or a garage shelf behaves like dry storage, and guessing beats making
    // the user classify it.
    case 'pantry':
    case 'other':
      return shelfLife.pantry;
  }
}

/**
 * When something the household keeps should be treated as past its useful life.
 *
 * Mirrors `expiry_of`. This is the answer that wins wherever the household has listed the
 * food, because it is the one they can go and correct; `inferExpiry` below is the fallback for
 * a food nobody has listed yet.
 *
 * Note what is missing: the location. A listed food keeps for the number of days the household
 * typed, wherever it ends up, because that number is about their food rather than about
 * shelves in general.
 */
export function expiryOf(grocery: Grocery, addedAt: Date, openedAt: Date | null): Date | null {
  const unopened = daysAfter(grocery.shelf_life_days, addedAt);
  const opened = openedAt === null ? null : daysAfter(grocery.opened_shelf_life_days, openedAt);

  return earlier(unopened, opened);
}

export interface Storing {
  food: Food;
  location: LocationKind;
  addedAt: Date;
  openedAt: Date | null;
}

/**
 * When this should be treated as past its useful life, or `null` when nothing applies.
 *
 * Staples have no expiry. The unopened window runs from when it arrived, the opened window
 * from when it was opened, and where both apply the earlier wins — opening food never extends
 * its life.
 */
export function inferExpiry(storing: Storing): Date | null {
  if (storing.food.staple) {
    return null;
  }

  const unopened = after(storing.food.shelf_life_days, storing.location, storing.addedAt);
  const opened =
    storing.openedAt === null || storing.food.opened_shelf_life_days === null
      ? null
      : after(storing.food.opened_shelf_life_days, storing.location, storing.openedAt);

  return earlier(unopened, opened);
}

function after(shelfLife: ShelfLife, location: LocationKind, from: Date): Date | null {
  return daysAfter(daysIn(shelfLife, location), from);
}

function daysAfter(days: number | null, from: Date): Date | null {
  return days === null ? null : new Date(from.getTime() + days * 86_400_000);
}

function earlier(left: Date | null, right: Date | null): Date | null {
  if (left === null) {
    return right;
  }
  if (right === null) {
    return left;
  }

  return left < right ? left : right;
}
